import React, { useState, useContext } from "react";
import * as api from "../../services/onlineQuotingService";
import {
  Panel,
  Switch,
  useForm,
  Form,
  LoadingIndicator,
  formatDate
} from "@ufginsurance/ui-kit";
import OnlineQuotingContext from "../OnlineQuotingContext";
import { useGetData } from "../shared/useGetData";
import "../shared/CoverageTitle.scss";

const HiredAutoToggles = () => {
  const {
    get,
    quoteData,
    updateQuote,
    supportingData,
    toastErrr,
    closeUpdatingToast,
    showUpdatingToast
  } = useContext(OnlineQuotingContext);

  const initialValues = {
    both_hno:
      !!supportingData?.packages?.hiredNonOwnedAuto?.hiredAuto &&
      !!supportingData?.packages?.hiredNonOwnedAuto?.nonOwnedAuto,
    hiredAuto: !!supportingData?.packages?.hiredNonOwnedAuto?.hiredAuto,
    nonOwnedAuto: !!supportingData?.packages?.hiredNonOwnedAuto?.nonOwnedAuto
  };

  const [previousValues, setPreviousValues] = useState(initialValues);

  const form = useForm({ values: initialValues });
  const { values, handleOnBlur, handleOnValidate, updateForm } = form;

  // on change event for HNO toggles
  const hnoTogglesOnChange = ({ field, value }) => {
    const newValues =
      field === "both_hno"
        ? { ...values, hiredAuto: value, nonOwnedAuto: value }
        : { ...values, [field]: value };

    if (!value) newValues.both_hno = false;
    if (newValues.hiredAuto && newValues.nonOwnedAuto)
      newValues.both_hno = true;

    const toastId = Math.random();
    const message = "Updating Hired and Non-owned Auto";
    showUpdatingToast({ message, toastId });

    updateForm({ values: newValues });

    api
      .saveHiredNonOwnedAuto(quoteData.quoteID, newValues)
      .then(result => {
        updateQuote({
          newData: result?.data?.dto,
          newSupportingData: result?.data?.supportingData,
          mergeData: false,
          sendToPC: false
        });
        setPreviousValues(newValues);
        closeUpdatingToast({ toastId });
      })
      .catch(error => {
        closeUpdatingToast({ toastId });
        toastErrr({
          action: "saveHiredNonOwnedAuto",
          description: "failure saving HNO",
          error,
          payload: { quoteId: quoteData.quoteID, values }
        });
        // if there was a problem with the api call, reset the form to what it was set before the call
        updateForm({ values: previousValues });
      });
  };

  const primaryLocation = get.location({ isPrimary: true });
  const primaryAddressState = primaryLocation?.address?.state;
  const effectiveDate = formatDate(quoteData?.baseData?.periodStartDate);

  const {
    data: productMatrix,
    loading: productMatrixIsLoading,
    error: productMatrixError
  } = useGetData({
    apiCall: () =>
      primaryAddressState &&
      api.getProductMatrixForState({
        stateAbr: primaryAddressState,
        useAWS: true
      }),
    deps: [effectiveDate, primaryLocation?.state]
  });

  /**
   * {
      "code": "CA7CommAutoLine",
      "effectiveDate": "01/01/2018",
      "legacyEffectiveDate": "01/01/2018",
      "onlineQuotingEffectiveDate": "04/01/2024",
      "expirationDate": null,
      "conflicts": [
        "GaragePro"
      ],
      "requires": [
        "BP7BusinessOwnersLine"
      ],
      "onlineQuotingEnabled": false,
      "onlineQuotingRequired": false
    },
   */
  // Extract product lookup into a helper function
  const findProduct = ({ products, productCode }) =>
    (products || []).find(p => p.code === productCode);

  // Extract date validation into a helper function
  const isDateInValidRange = ({ effectiveDate, startDate, expireDate }) => {
    const date = new Date(effectiveDate);
    const start = startDate ? new Date(startDate) : null;
    const expired = expireDate ? new Date(expireDate) : null;
    return date >= start && (!expired || date <= expired);
  };

  const commAutoProduct = findProduct({
    products: productMatrix?.products,
    productCode: "CA7CommAutoLine"
  });

  const commAutoEnabled =
    effectiveDate &&
    commAutoProduct &&
    isDateInValidRange({
      effectiveDate,
      startDate: commAutoProduct?.onlineQuotingEffectiveDate,
      expireDate: commAutoProduct?.expirationDate
    });

  if (productMatrixIsLoading) return <LoadingIndicator />;

  if (productMatrixError)
    return (
      <div className="oq__hno-panel__error">
        <p>Error Loading HNO panel</p>
      </div>
    );

  if (!commAutoEnabled) return null;

  return (
    <Form context={form}>
      <Panel className="oq__hno-panel">
        {
          <>
            <Switch
              id="both_hno"
              name="both_hno"
              label="Hired and Non-owned Auto (Add Both)"
              noLabel
              className="oq__hno-both-switch"
              onChange={hnoTogglesOnChange}
              onBlur={handleOnBlur}
              onValidate={handleOnValidate}
              value={values.both_hno}
              size="auto"
            />
            <Switch
              id="hiredAuto"
              name="hiredAuto"
              label="Hired Auto"
              onChange={hnoTogglesOnChange}
              onBlur={handleOnBlur}
              onValidate={handleOnValidate}
              value={values.hiredAuto}
              size="auto"
            />
            <Switch
              id="nonOwnedAuto"
              name="nonOwnedAuto"
              label="Non-owned Auto"
              onChange={hnoTogglesOnChange}
              onBlur={handleOnBlur}
              onValidate={handleOnValidate}
              value={values.nonOwnedAuto}
              size="auto"
            />
          </>
        }
      </Panel>
    </Form>
  );
};

export default HiredAutoToggles;
